<template>
  <div class="entra-page">
    <EntraPageHeader
      title="Settings"
      :tenantDisplayName="tenantName"
      :primaryDomain="primaryDomain"
      :regionCode="regionCode"
      :tenantStatus="tenantStatus"
      :freshnessState="freshnessState"
      :lastCollection="lastCollection"
      :dataCompleteness="dataCompleteness"
      :capabilities="capabilities"
      :showSearch="false"
      :showExport="false"
      :showRefresh="false"
      :showSavedViews="false"
      :showBreadcrumb="true"
    />

    <div class="page-body">
      <div v-if="error" class="error-message">{{ error }}</div>
      <div class="settings-grid">
        <div v-for="section in sections" :key="section.key" class="settings-card">
          <div class="settings-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
          </div>
          <h3 class="settings-name">{{ section.label }}</h3>
          <p class="settings-desc">{{ section.description }}</p>
          <span class="settings-status" :class="section.status === 'configured' ? 'status-ok' : 'status-pending'">
            {{ section.status === 'configured' ? 'Configured' : 'Not Configured' }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';

export default {
  name: 'EntraTenantSettings',
  components: { EntraPageHeader },
  data: function() {
    return {
      tenantId: '', tenantName: '', primaryDomain: '', regionCode: '', tenantStatus: '',
      freshnessState: 'fresh', lastCollection: '', dataCompleteness: null, capabilities: [],
      error: '',
      sections: [
        { key: 'connection', label: 'Connection & Consent', description: 'Manage Graph API consent and tenant connectivity', status: 'configured' },
        { key: 'collection', label: 'Collection Schedule', description: 'Configure collection frequency and resource types', status: 'configured' },
        { key: 'capabilities', label: 'Enabled Capabilities', description: 'Choose which Entra features to enable for this tenant', status: 'configured' },
        { key: 'licence-optimisation', label: 'Licence Optimisation Policy', description: 'Inactivity thresholds, exclusions and rules', status: 'pending' },
        { key: 'classifications', label: 'User Classifications', description: 'Manage service accounts, break-glass and shared accounts', status: 'pending' },
        { key: 'exclusions', label: 'Exclusions', description: 'Manage finding exclusions by user, group, rule or scope', status: 'pending' },
        { key: 'pricing', label: 'Pricing', description: 'Configure per-SKU pricing and billing terms', status: 'pending' },
        { key: 'health-score', label: 'Health Score', description: 'Configure score domains, weights and rules', status: 'pending' },
        { key: 'notifications', label: 'Notifications', description: 'Alert thresholds and notification channels', status: 'pending' },
        { key: 'retention', label: 'Retention', description: 'Data retention periods by data category', status: 'pending' },
        { key: 'remediation', label: 'Remediation Approval', description: 'Approval workflow configuration for write actions', status: 'pending' },
        { key: 'audit', label: 'Audit & Data Export', description: 'Audit log and bulk data export controls', status: 'pending' }
      ]
    };
  },
  methods: {
    loadData: function() {
      this.tenantId = this.$route.params.tenantId || '';
      if (!this.tenantId) {
        this.error = 'Please select a tenant from the Portfolio page first.';
        return;
      }
    }
  },
  mounted: function() { this.loadData(); },
  watch: { '$route.params.tenantId': 'loadData' }
};
</script>

<style scoped>
.entra-page { min-height: 100%; }
.page-body { padding: 24px; }

.error-message {
  padding: 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.settings-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s, border-color 0.2s;
  cursor: pointer;
}
.settings-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.06); border-color: #94a3b8; }

.settings-icon { width: 28px; height: 28px; color: #64748b; margin-bottom: 10px; }
.settings-icon svg { width: 100%; height: 100%; }

.settings-name { font-size: 0.9375rem; font-weight: 600; color: #1e293b; margin: 0 0 4px 0; }
.settings-desc { font-size: 0.75rem; color: #94a3b8; margin: 0 0 12px 0; flex: 1; }

.settings-status {
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 10px;
  align-self: flex-start;
}
.status-ok { background: #dcfce7; color: #166534; }
.status-pending { background: #f1f5f9; color: #64748b; }
</style>
